import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import type { VpnPolicy } from '../api';
import { PolicySheet } from '../components/PolicySheet';
import { minutesLabel, placesLabel } from '../format';
import { usePolicies } from '../hooks';
import { useVpnPermissions } from '../permissions';

const columns: ResponsiveTableColumn<VpnPolicy>[] = [
  { key: 'name', label: 'Policy', hideMobileLabel: true, render: (p) => p.name },
  {
    key: 'state',
    label: 'State',
    render: (p) =>
      p.enabled ? (
        <Badge className="border-0 bg-sev-4/15 text-sev-4">On</Badge>
      ) : (
        <Badge className="border-0 bg-muted text-muted-foreground">Off</Badge>
      ),
  },
  { key: 'teams', label: 'Teams', render: (p) => p.teams.map((t) => t.name).join(', ') || '—' },
  { key: 'scope', label: 'Where', render: (p) => placesLabel(p) },
  { key: 'length', label: 'Longest session', hideAt: 'mobile', render: (p) => minutesLabel(p.maxSessionMinutes) },
];

/**
 * Who may connect where (docs/VpnAccess.md, "Policies"). Default deny: with no
 * policy, nobody connects. There are no deny rules; policies add up.
 */
export function VpnPolicies() {
  const { canUpdateVpn } = useVpnPermissions();
  const { data, isLoading, isError } = usePolicies();
  const [open, setOpen] = useState<VpnPolicy | 'new' | null>(null);

  return (
    <PageContainer width="full">
      <PageHeader
        title="Policies"
        subtitle="Who may connect to which sites. Nobody connects without a policy; policies add up."
        primary={
          <Button
            disabled={!canUpdateVpn}
            title={canUpdateVpn ? undefined : 'Making a policy takes vpn:update'}
            onClick={() => {
              setOpen('new');
            }}
          >
            New policy
          </Button>
        }
      />
      <ResponsiveTable
        columns={columns}
        rows={data ?? []}
        rowKey={(p) => p.id}
        isLoading={isLoading}
        onRowClick={(p) => {
          setOpen(p);
        }}
        empty={
          isError ? (
            <EmptyState variant="error" size="inline" title="Couldn't load the policies" />
          ) : (
            <EmptyState
              size="inline"
              title="No policies"
              description="Until a policy names their team, nobody can connect through the VPN."
            />
          )
        }
      />
      {open ? (
        <PolicySheet
          key={open === 'new' ? 'new' : open.id}
          policy={open === 'new' ? null : open}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </PageContainer>
  );
}
